/**
 * CeroHalo · movimiento.
 * Solo corre si el tema tiene el movimiento activado y el sistema no pide reducirlo
 * (clase html.mt-motion-on, puesta en layout/theme.liquid).
 * - Apariciones al hacer scroll: una sola vez, con escalonado corto. Todo es visible por defecto:
 *   solo se ocultan (y se revelan) los elementos que aún están por debajo de la pantalla.
 * - Modo de bajo consumo: en móviles modestos o con ahorro de datos se desactiva el efecto del hero.
 */

const root = document.documentElement;

function lowPower() {
  const nav = /** @type {Navigator & { deviceMemory?: number, connection?: { saveData?: boolean } }} */ (navigator);
  if (nav.connection?.saveData) return true;
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) return true;
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 4) return true;
  return false;
}

if (root.classList.contains('mt-motion-on') && lowPower()) {
  root.classList.add('mt-low-power');
}

const REVEAL_SELECTOR = [
  '.mt-section:not(.mt-hero) .mt-stack',
  '.mt-sintomas__card',
  '.mt-causa__side',
  '.mt-pasos__step',
  '.mt-video__media',
  '.mt-hace__sheet',
  '.mt-usos__item',
  '.mt-offer',
  '.mt-garantia__item',
  '.mt-faq__list',
  '.mt-cierre__inner',
].join(',');

function initReveal() {
  if (!root.hasAttribute('data-mt-reveal-on')) return; // solo existe con mt-motion-on
  if (!('IntersectionObserver' in window)) return;
  const candidates = document.querySelectorAll(REVEAL_SELECTOR);
  if (!candidates.length) return;

  /** @type {Map<Element, number>} */
  const groups = new Map();
  const seen = new WeakSet();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = /** @type {HTMLElement} */ (entry.target);
        if (!seen.has(el)) {
          seen.add(el);
          // Primera medición (sin forzar maquetación): lo que ya se ve, o queda por encima, no se toca.
          if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
            observer.unobserve(el);
            continue;
          }
          // Escalonado entre hermanos del mismo grupo: 60 ms, máximo 5 pasos.
          const index = groups.get(el.parentElement) ?? 0;
          groups.set(el.parentElement, index + 1);
          el.style.transitionDelay = `${Math.min(index, 5) * 60}ms`;
          el.setAttribute('data-mt-reveal', 'pending');
          continue;
        }
        if (!entry.isIntersecting) continue;
        el.setAttribute('data-mt-reveal', 'done');
        observer.unobserve(el);
        el.addEventListener('transitionend', () => (el.style.transitionDelay = ''), { once: true });
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );
  for (const el of candidates) observer.observe(el);
}

function initAutoplay() {
  const videos = /** @type {HTMLVideoElement[]} */ ([...document.querySelectorAll('video.mt-autoplay')]);
  if (!videos.length) return;
  const canPlay = root.classList.contains('mt-motion-on') && !root.classList.contains('mt-low-power');
  if (!canPlay || !('IntersectionObserver' in window)) {
    for (const video of videos) {
      if (!video.closest('.mt-hero__video')) video.controls = true; // el fondo del hero se queda quieto
    }
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      }
    },
    { threshold: 0.25 }
  );
  // El vídeo de fondo del hero espera a que cargue la página, para no competir con lo primero que se ve.
  const start = () => {
    for (const video of videos) observer.observe(video);
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

/* Enlaces a una sección de la misma página («Ver ofertas» baja miles de píxeles): desplazamiento
   suave para no perder la referencia. Solo con movimiento activado (mt-motion-on ya respeta
   «reducir movimiento»). */
function initAnchors() {
  if (!root.classList.contains('mt-motion-on')) return;
  document.addEventListener('click', (event) => {
    const link = /** @type {HTMLElement} */ (event.target).closest?.('a[href*="#"]');
    if (!(link instanceof HTMLAnchorElement) || link.pathname !== location.pathname || !link.hash) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Como el salto nativo: el teclado sigue desde la sección de destino.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    history.pushState(null, '', link.hash);
  });
}

function init() {
  initReveal();
  initAutoplay();
  initAnchors();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// En el editor de Shopify: al recargar una sección, se muestra al instante.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  for (const el of target.querySelectorAll('[data-mt-reveal]')) el.setAttribute('data-mt-reveal', 'done');
});
